import { defer } from 'react-router-dom'
import { getQueryStringParamsFromStringUrl, logger } from '$utils'
import { isAppStateReady } from 'src/state/store'
import { shiftRequestService } from '$services'
import { ShiftRequestErrorId, ShiftRequestStatus } from '$constants'
import { CoreApiError, ShiftRequest } from '$models'

/**
 * What the shift request status page renders from. At most one of `shiftRequest` (the update went
 * through) and `alreadyResolvedMessage` (someone else resolved the request first) is set; when
 * both are missing the update failed for another reason and the page shows a full page error.
 */
export interface ShiftRequestStatusPageData {
  shiftRequest?: ShiftRequest
  alreadyResolvedMessage?: string
  /** Shift date formatted in the company time zone, carried by the email link. */
  shiftDate?: string
  /** Shift time range formatted in the company time zone, carried by the email link. */
  shiftTime?: string
  /** Counter shift date formatted in the company time zone, carried by the email link on a swap. */
  counterShiftDate?: string
  /** Counter shift time range formatted in the company time zone, carried by the email link on a swap. */
  counterShiftTime?: string
}

export default async function loader({ request: { url } }) {
  const queryParams = getQueryStringParamsFromStringUrl(url)

  logger.debug(
    '[loader] About to await AppState being ready in employee Shift Request Status loader'
  )
  await isAppStateReady()
  logger.debug(
    '[loader] AppState should now be ready in employee Shift Request Status loader'
  )

  // Grab url params. The shift date and time arrive pre-formatted in the company time zone:
  // this page is opened via a one-time token without an authenticated session, so it cannot
  // resolve the company time zone itself.
  const {
    requestId,
    status,
    shiftDate,
    shiftTime,
    counterShiftDate,
    counterShiftTime,
  } = queryParams

  // NOTE that it is expected requests to this loader will happen on a page
  // with a one time token. This means you can't make API calls to our normal
  // backend as the user will most likely _not_ be authenticated. Data that
  // needs to be displayed can be included as url query params where appropriate.

  // Immediately process the status update; the single API call's response is
  // used for the display on the page about to be loaded.
  const data: Promise<ShiftRequestStatusPageData> = shiftRequestService
    .updateStatus(requestId, status as unknown as ShiftRequestStatus)
    .then((shiftRequest) => ({
      shiftRequest,
      shiftDate,
      shiftTime,
      counterShiftDate,
      counterShiftTime,
    }))
    .catch((error) => {
      // The approval email goes to every approver in the venue, so whoever clicks second lands
      // here. Pass the API's explanation through instead of showing an error page.
      if (
        error instanceof CoreApiError &&
        error.id === ShiftRequestErrorId.StatusConflict
      ) {
        return {
          alreadyResolvedMessage:
            error.messages?.[0] ?? 'This request has already been resolved',
          shiftDate,
          shiftTime,
        }
      }
      logger.error('Employee Shift Request Status update error', error)
      return {}
    })

  return defer({ data })
}
